import { useCallback, useEffect, useState } from "react"

import {
  ErroDaApi,
  api,
  contaGuardada,
  type ContaNaTela,
  type EntidadeNaLista,
  type PapelNaTela,
  type SetorNaTela,
} from "../api"
import { algarismosDoCpf, cpfValido, dataHora, mascaraDeCpf } from "../formato"
import {
  FormularioDeCampos,
  diferenca,
  faltaNoBloco,
  mensagemDeRecusa,
  type CampoDoBloco,
  type ValoresDoBloco,
} from "../componentes/BlocoDeCampos"
import { Button } from "../componentes/Button"
import { Card, CardContent, CardHeader, CardTitle } from "../componentes/Card"
import { Checkbox } from "../componentes/Checkbox"
import { Escolha } from "../componentes/Escolha"
import { FiltroAplicado } from "../componentes/FiltroAplicado"
import { MarcaDeObrigatorio, RotuloDoCampo } from "../componentes/RotuloDoCampo"
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../componentes/Dialog"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "../componentes/DropdownMenu"
import { Input } from "../componentes/Input"
import { PasswordInput } from "../componentes/PasswordInput"
import { Selo } from "../componentes/Selo"
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "../componentes/Sheet"
import { Acao, Column, TablePrincipal2, TablePrincipalFiltroGlobal } from "../componentes/TablePrincipal2"
import { Abas, type DescricaoDeAba } from "../componentes/Abas"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogBody,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../componentes/AlertDialog"
import { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "../componentes/Empty"
import { Skeleton } from "../componentes/Skeleton"
import { toast } from "../componentes/Toast"
import { ConfirmarComSenha, ResumoDoAto } from "../componentes/ConfirmarComSenha"
import * as Icones from "../componentes/Icones"
import {
  BOTAO_BRANCO,
  BOTAO_CANCELAR,
  CABECALHO_DO_SHEET,
  MAIUSCULA,
  PROCEDENCIA,
  RECUSA,
  ROTULO_DE_CAMPO,
  VAZIO,
} from "../lib/classes"
import { cn } from "../lib/cn"
import { comListasDoVinculo, opcoesDosCargos, opcoesDosSetores, pedidoDoVinculo, valoresComIds } from "../lib/vinculo"

const semAcento = (texto: string) =>
  texto
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()

type AbaDasContas = "todas" | "ativas" | "inativas"

const ABAS_DAS_CONTAS: DescricaoDeAba<AbaDasContas>[] = [
  { chave: "todas", rotulo: "Todas", dica: "Mostra todas as contas" },
  { chave: "ativas", rotulo: "Ativas", dica: "Mostra só as contas que entram no sistema" },
  { chave: "inativas", rotulo: "Inativas", dica: "Mostra só as contas desativadas" },
]

type GavetaDasContas = { tipo: "nova" } | { tipo: "dados"; conta: ContaNaTela } | { tipo: "senha"; conta: ContaNaTela } | null

const POR_PAGINA_DAS_CONTAS = 10

const RODAPE_DA_GAVETA = "mt-2 flex justify-end gap-2 border-t border-(--borda) pt-4"

type Filtros = { perfil: string; entidade: string }

const SEM_FILTRO: Filtros = { perfil: "", entidade: "" }

const FILTRO_DA_BARRA = "w-full min-w-0 md:w-48 md:flex-none"

export function Contas({
  perfil,
  aoFiltrarPerfil,
  cargo,
  aoFiltrarCargo,
}: {
  perfil?: string
  aoFiltrarPerfil?: (perfil: string | null) => void
  cargo?: string
  aoFiltrarCargo?: (cargo: string | null) => void
} = {}) {
  const [contas, definirContas] = useState<ContaNaTela[] | null>(null)
  const [papeis, definirPapeis] = useState<PapelNaTela[]>([])
  const [podeAdministrar, definirPode] = useState(false)
  const [falhaAoCarregar, definirFalhaAoCarregar] = useState<string | null>(null)
  const [recusaNaGaveta, definirRecusaNaGaveta] = useState<string | null>(null)
  const [gaveta, definirGaveta] = useState<GavetaDasContas>(null)
  const [desativando, definirDesativando] = useState<ContaNaTela | null>(null)
  const [busca, definirBusca] = useState("")
  const [aba, definirAba] = useState<AbaDasContas>("todas")
  const [filtros, definirFiltros] = useState<Filtros>({ ...SEM_FILTRO, perfil: perfil ?? "" })
  const [pagina, definirPagina] = useState(1)
  const [porPagina, definirPorPagina] = useState(POR_PAGINA_DAS_CONTAS)
  const eRoot = contaGuardada()?.is_root === true

  const carregar = useCallback(() => {
    definirFalhaAoCarregar(null)
    api
      .contas()
      .then((resposta) => {
        definirContas(resposta.dados)
        definirPapeis(resposta.papeis)
        definirPode(resposta.pode_administrar)
      })
      .catch((falha: unknown) => definirFalhaAoCarregar(mensagemDeRecusa(falha)))
  }, [])

  useEffect(carregar, [carregar])

  useEffect(() => {
    definirFiltros((atuais) => (atuais.perfil === (perfil ?? "") ? atuais : { ...atuais, perfil: perfil ?? "" }))
    definirPagina(1)
  }, [perfil])

  if (contas === null) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Contas e permissões</CardTitle>
        </CardHeader>
        {falhaAoCarregar !== null ? (
          <CardContent className="flex flex-wrap items-center gap-3">
            <p role="alert" className={cn(RECUSA, MAIUSCULA)}>
              {falhaAoCarregar}
            </p>
            <Button type="button" variant="outline" size="sm" className={BOTAO_BRANCO} onClick={carregar}>
              <Icones.Recalcular />
              Tentar de novo
            </Button>
          </CardContent>
        ) : (
          <CardContent aria-busy="true" className="flex flex-col gap-3">
            <Skeleton className="h-10 w-1/2" />
            {[0, 1, 2, 3, 4].map((linha) => (
              <Skeleton key={linha} className="h-9 w-full" />
            ))}
          </CardContent>
        )}
      </Card>
    )
  }

  const procurada = semAcento(busca.trim())
  const filtradas = contas.filter(
    (conta) =>
      (aba === "todas" || (aba === "ativas") === conta.ativo) &&
      (filtros.perfil === "" || perfisDe(conta).includes(filtros.perfil)) &&
      (filtros.entidade === "" || conta.entidade === filtros.entidade) &&
      (cargo === undefined || conta.cargo === cargo) &&
      (procurada === "" ||
        [conta.nome, conta.email, conta.entidade ?? ""].some((texto) => semAcento(texto).includes(procurada))),
  )
  const totalDePaginas = Math.max(1, Math.ceil(filtradas.length / porPagina))
  const paginaAtual = Math.min(pagina, totalDePaginas)
  const recorte = filtradas.slice((paginaAtual - 1) * porPagina, paginaAtual * porPagina)
  const entidadesDasContas = [...new Set(contas.map((c) => c.entidade).filter((e): e is string => e !== null && e !== undefined))].sort()
  const comFiltro = filtros.perfil !== "" || filtros.entidade !== "" || cargo !== undefined

  const filtrar = (novos: Filtros) => {
    if (novos.perfil !== filtros.perfil) aoFiltrarPerfil?.(novos.perfil === "" ? null : novos.perfil)
    definirFiltros(novos)
    definirPagina(1)
  }

  const mudar = (conta: ContaNaTela, corpo: { perfis?: string[]; ativo?: boolean }, sucesso: string) => {
    api
      .mudarConta(conta.id, corpo)
      .then(() => {
        toast.success(sucesso)
        carregar()
      })
      .catch((falha: unknown) => toast.error(mensagemDeRecusa(falha)))
  }

  const abrirGaveta = (nova: GavetaDasContas) => {
    definirRecusaNaGaveta(null)
    definirGaveta(nova)
  }

  const fecharGaveta = () => {
    definirGaveta(null)
    definirRecusaNaGaveta(null)
  }

  const depoisDaGaveta = (mensagem: string) => {
    fecharGaveta()
    toast.success(mensagem)
    carregar()
  }

  const novaConta = (
    <Button type="button" className="h-10" onClick={() => abrirGaveta({ tipo: "nova" })}>
      <Icones.Mais />
      Nova conta
    </Button>
  )

  const vazio =
    contas.length === 0 ? (
      <Empty className="py-8">
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <Icones.Pessoas />
          </EmptyMedia>
          <EmptyTitle>Nenhuma conta cadastrada</EmptyTitle>
          <EmptyDescription>
            {podeAdministrar ? "Crie a primeira conta de quem vai usar o sistema." : "Quem administra a entidade cria as contas."}
          </EmptyDescription>
        </EmptyHeader>
        {podeAdministrar && <EmptyContent>{novaConta}</EmptyContent>}
      </Empty>
    ) : (
      <Empty className="py-8">
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <Icones.Buscar />
          </EmptyMedia>
          <EmptyTitle>Nenhuma conta encontrada</EmptyTitle>
          <EmptyDescription>
            {busca !== "" ? `Nada casa com "${busca}"${comFiltro ? " nos filtros escolhidos" : ""}.` : "Nenhuma conta nesta situação ou nos filtros escolhidos."}
          </EmptyDescription>
        </EmptyHeader>
        {(busca !== "" || comFiltro) && (
          <EmptyContent>
            {busca !== "" && (
              <Button type="button" variant="outline" size="sm" className={BOTAO_BRANCO} onClick={() => definirBusca("")}>
                Limpar a busca
              </Button>
            )}
            {comFiltro && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className={BOTAO_BRANCO}
                onClick={() => {
                  filtrar(SEM_FILTRO)
                  if (cargo !== undefined) aoFiltrarCargo?.(null)
                }}
              >
                Limpar filtros
              </Button>
            )}
          </EmptyContent>
        )}
      </Empty>
    )

  return (
    <Card>
      <CardHeader>
        <CardTitle>Contas e permissões</CardTitle>
      </CardHeader>

      <CardContent>
        <TablePrincipal2
          data={recorte}
          pageCount={totalDePaginas}
          currentPage={paginaAtual}
          pageSize={porPagina}
          totalRegistros={filtradas.length}
          onPageChange={definirPagina}
          onPageSizeChange={(tamanho) => {
            definirPorPagina(tamanho)
            definirPagina(1)
          }}
          globalFilterValue={busca}
          onGlobalFilterChange={(valor) => {
            definirBusca(valor)
            definirPagina(1)
          }}
          mensagemVazia={vazio}
          listrada
          acaoNoTopo={podeAdministrar && novaConta}
          aoClicarNaLinha={podeAdministrar ? (conta) => abrirGaveta({ tipo: "dados", conta }) : undefined}
          linhaClicavel={(conta) => !conta.is_root}
          abasNaBarra={
            <Abas
              nome="contas"
              rotulo="Situação das contas"
              classe="mb-0"
              abas={ABAS_DAS_CONTAS}
              atual={aba}
              aoTrocar={(chave) => {
                definirAba(chave)
                definirPagina(1)
              }}
            />
          }
          filtrosNaBarra={
            <>
              {cargo !== undefined && <FiltroAplicado rotulo="Cargo" valor={cargo} aoLimpar={() => aoFiltrarCargo?.(null)} />}
              <Escolha
                rotulo="Filtrar pelo perfil"
                classeDaCaixa={FILTRO_DA_BARRA}
                valor={filtros.perfil}
                aoMudar={(perfil) => filtrar({ ...filtros, perfil })}
                vazio="Todos os perfis"
                opcoes={papeis.map((p) => ({ valor: p.papel, rotulo: p.nome }))}
              />
              {eRoot && (
                <Escolha
                  rotulo="Filtrar pela entidade"
                  classeDaCaixa={FILTRO_DA_BARRA}
                  valor={filtros.entidade}
                  aoMudar={(entidade) => filtrar({ ...filtros, entidade })}
                  vazio="Todas as entidades"
                  placeholderDaBusca="Buscar pela sigla"
                  opcoes={entidadesDasContas.map((e) => ({ valor: e, rotulo: e }))}
                />
              )}
            </>
          }
        >
          <TablePrincipalFiltroGlobal globalFilter={busca} setGlobalFilter={definirBusca} placeholder="Nome, e-mail ou entidade" />
          <Column<ContaNaTela>
            name="nome"
            label="Nome e e-mail"
            enableSorting={false}
            render={(_valor, conta) => (
              <span className="flex min-w-0 flex-col gap-0.5">
                <span className="flex flex-wrap items-center gap-1.5">
                  <span className="font-medium">{conta.nome}</span>
                  {conta.is_root && <Selo situacao="root" />}
                </span>
                <span className="text-(length:--texto-xs) break-all text-(--tinta-2)">{conta.email}</span>
              </span>
            )}
          />
          <Column<ContaNaTela> name="entidade" label="Entidade" enableSorting={false} render={(valor) => String(valor ?? "—")} />
          <Column<ContaNaTela>
            name="cpf_mascarado"
            label="CPF"
            enableSorting={false}
            render={(valor) => <span className="whitespace-nowrap">{String(valor ?? "—")}</span>}
          />
          <Column<ContaNaTela>
            name="cargo"
            label="Cargo"
            enableSorting={false}
            render={(valor) => <span className={cn("inline-block", MAIUSCULA)}>{String(valor ?? "—")}</span>}
          />
          <Column<ContaNaTela>
            name="perfis"
            label="Perfis"
            enableSorting={false}
            render={(_valor, conta) => (
              <PerfisDaConta
                conta={conta}
                papeis={papeis}
                podeAdministrar={podeAdministrar}
                aoMudar={(perfis) => mudar(conta, { perfis }, `Perfis de ${conta.nome} atualizados`)}
              />
            )}
          />
          <Column<ContaNaTela>
            name="ativo"
            label="Situação"
            enableSorting={false}
            render={(_valor, conta) => <Selo situacao={conta.ativo ? "ativa" : "inativa"} />}
          />
          <Column<ContaNaTela>
            name="senha_provisoria"
            label="Senha"
            enableSorting={false}
            render={(_valor, conta) => (
              <Selo situacao={conta.senha_provisoria ? "senha_provisoria" : "senha_do_dono"}>
                {conta.senha_provisoria
                  ? "Provisória"
                  : conta.senha_alterada_em === null
                    ? "Nunca trocada"
                    : `Trocada em ${dataHora(conta.senha_alterada_em)}`}
              </Selo>
            )}
          />
          {podeAdministrar && (
            <Acao<ContaNaTela>
              label="Dados funcionais"
              icon={Icones.Anotacao}
              visivel={(conta) => !conta.is_root}
              onClick={(conta) => abrirGaveta({ tipo: "dados", conta })}
            />
          )}
          {podeAdministrar && (
            <Acao<ContaNaTela> label="Redefinir senha" icon={Icones.Chave} onClick={(conta) => abrirGaveta({ tipo: "senha", conta })} />
          )}
          {podeAdministrar && (
            <Acao<ContaNaTela> label="Desativar" icon={Icones.Dispensar} visivel={(conta) => conta.ativo} onClick={definirDesativando} />
          )}
          {podeAdministrar && (
            <Acao<ContaNaTela>
              label="Reativar"
              icon={Icones.Reabrir}
              visivel={(conta) => !conta.ativo}
              onClick={(conta) => mudar(conta, { ativo: true }, `Conta de ${conta.nome} reativada`)}
            />
          )}
        </TablePrincipal2>
      </CardContent>

      <AlertDialog open={desativando !== null} onOpenChange={(aberto) => !aberto && definirDesativando(null)}>
        <AlertDialogContent>
          <AlertDialogHeader tom="perigo">
            <AlertDialogTitle>Desativar a conta de {desativando?.nome}?</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogBody>
            <AlertDialogDescription>
              {eRoot
                ? "A pessoa deixa de entrar no sistema em todas as entidades. É possível reativar depois."
                : "A pessoa deixa de entrar só nesta entidade. Se ela trabalha em outras, o acesso a elas continua. É possível reativar depois."}
            </AlertDialogDescription>
          </AlertDialogBody>
          <AlertDialogFooter>
            <AlertDialogCancel asChild>
              <Button type="button" variant="outline">
                <Icones.Fechar />
                Cancelar
              </Button>
            </AlertDialogCancel>
            <AlertDialogAction asChild>
              <Button
                type="button"
                variant="destructive"
                onClick={() => {
                  if (desativando !== null) mudar(desativando, { ativo: false }, `Conta de ${desativando.nome} desativada`)
                  definirDesativando(null)
                }}
              >
                <Icones.Dispensar />
                Desativar
              </Button>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Sheet open={gaveta !== null} onOpenChange={(aberta) => !aberta && fecharGaveta()}>
        <SheetContent
          {...(gaveta?.tipo === "senha" ? {} : { "aria-describedby": undefined })}
          className="w-full gap-0 overflow-y-auto bg-card p-0 sm:max-w-lg [&>button:last-child]:text-primary-foreground [&>button:last-child]:opacity-100"
        >
          <SheetHeader className={CABECALHO_DO_SHEET}>
            <SheetTitle className="text-primary-foreground">
              {gaveta?.tipo === "nova"
                ? "Nova conta"
                : gaveta?.tipo === "dados"
                  ? `Dados funcionais: ${gaveta.conta.nome}`
                  : "Redefinir senha"}
            </SheetTitle>
            {gaveta?.tipo === "senha" && (
              <SheetDescription className="text-primary-foreground">Uma senha provisória para {gaveta.conta.nome}</SheetDescription>
            )}
          </SheetHeader>
          <div data-slot="corpo-da-gaveta" className="flex flex-col gap-4 px-6 pt-3 pb-6">
            {recusaNaGaveta !== null && (
              <p role="alert" className={cn(RECUSA, "mt-0", MAIUSCULA)}>
                {recusaNaGaveta}
              </p>
            )}
            {gaveta?.tipo === "nova" && (
              <NovaConta papeis={papeis} eRoot={eRoot} aoCriar={depoisDaGaveta} aoCancelar={fecharGaveta} aoRecusar={definirRecusaNaGaveta} />
            )}
            {gaveta?.tipo === "dados" && (
              <DadosFuncionaisDaConta
                contaId={gaveta.conta.id}
                aoGravar={() => depoisDaGaveta(`Dados de ${gaveta.conta.nome} salvos`)}
                aoFechar={fecharGaveta}
              />
            )}
            {gaveta?.tipo === "senha" && (
              <RedefinirSenha
                conta={gaveta.conta}
                aoSalvar={() => depoisDaGaveta(`Senha provisória de ${gaveta.conta.nome} definida`)}
                aoCancelar={fecharGaveta}
              />
            )}
          </div>
        </SheetContent>
      </Sheet>
    </Card>
  )
}

export const perfisDe = (conta: ContaNaTela) => (conta.perfis !== undefined && conta.perfis.length > 0 ? conta.perfis : [conta.papel])

const perfisOferecidos = (papeis: PapelNaTela[]) => papeis.filter((p) => p.papel !== "root")

function PerfisDaConta({
  conta,
  papeis,
  podeAdministrar,
  aoMudar,
}: {
  conta: ContaNaTela
  papeis: PapelNaTela[]
  podeAdministrar: boolean
  aoMudar: (perfis: string[]) => void
}) {
  const perfis = conta.perfis !== undefined && conta.perfis.length > 0 ? conta.perfis : [conta.papel]
  const rotuloDe = (papel: string) => papeis.find((p) => p.papel === papel)?.nome ?? papel
  const rotulos = perfis.map(rotuloDe).join(", ")

  if (!podeAdministrar || conta.is_root) return <>{rotulos}</>

  const alternar = (papel: string, marcado: boolean) => {
    const novos = marcado ? [...perfis.filter((p) => p !== papel), papel] : perfis.filter((p) => p !== papel)
    if (novos.length === 0) return
    aoMudar(novos)
  }

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={cn("h-7 max-w-[240px] justify-start text-xs", BOTAO_BRANCO)}
          aria-label={`Perfis de ${conta.nome}: ${rotulos}`}
          title="Escolhe os perfis desta pessoa. Cada marca grava na hora"
        >
          <span className="truncate">{rotulos}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-80">
        <DropdownMenuLabel>Perfis de {conta.nome}</DropdownMenuLabel>
        {perfisOferecidos(papeis).map((p) => {
          const marcado = perfis.includes(p.papel)
          const ultimo = marcado && perfis.length === 1
          return (
            <DropdownMenuCheckboxItem
              key={p.papel}
              checked={marcado}
              disabled={ultimo}
              title={ultimo ? "A conta precisa de pelo menos um perfil" : undefined}
              onSelect={(evento) => evento.preventDefault()}
              onCheckedChange={(valor) => alternar(p.papel, valor === true)}
            >
              <span className="flex min-w-0 flex-col">
                <span>{p.nome}</span>
                <span className={cn("text-(length:--texto-xs) text-(--tinta-2)", MAIUSCULA)}>{p.pode}</span>
              </span>
            </DropdownMenuCheckboxItem>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function RedefinirSenha({
  conta,
  aoSalvar,
  aoCancelar,
}: {
  conta: ContaNaTela
  aoSalvar: () => void
  aoCancelar: () => void
}) {
  const [senhaNova, definirSenhaNova] = useState("")
  const [confirmando, definirConfirmando] = useState(false)
  const id = `senha-nova-${conta.id}`

  return (
    <>
      <div>
        <RotuloDoCampo htmlFor={id} obrigatorio>Senha provisória</RotuloDoCampo>
        <PasswordInput
          id={id}
          name={id}
          required
          aria-required="true"
          autoComplete="new-password"
          value={senhaNova}
          onChange={(evento) => definirSenhaNova(evento.target.value)}
        />
      </div>
      <p className={PROCEDENCIA}>
        Define uma senha provisória, as sessões abertas da conta são encerradas e o ato da
        mudança fica registrado.
      </p>
      <div className={RODAPE_DA_GAVETA}>
        <Button type="button" variant="outline" className={BOTAO_CANCELAR} onClick={aoCancelar}>
          <Icones.Fechar />
          Cancelar
        </Button>
        <Button
          title={senhaNova === "" ? "Falta a senha provisória" : undefined}
          type="button"
          disabled={confirmando || senhaNova === ""}
          onClick={() => definirConfirmando(true)}
        >
          <Icones.Salvar />
          Salvar
        </Button>
      </div>
      <ConfirmarComSenha
        aberto={confirmando}
        titulo={`Redefinir a senha de ${conta.nome}`}
        resumo={
          <ResumoDoAto
            itens={[
              ["Conta", conta.nome],
              ["Depois", "A conta volta à senha provisória e as sessões abertas são encerradas"],
            ]}
          />
        }
        rotuloDoAto="Redefinir"
        iconeDoAto={<Icones.Salvar />}
        aoConfirmar={(senha) => api.redefinirSenha(conta.id, senhaNova, senha).then(aoSalvar)}
        aoFechar={() => definirConfirmando(false)}
      />
    </>
  )
}

function DadosFuncionaisDaConta({
  contaId,
  aoGravar,
  aoFechar,
}: {
  contaId: number
  aoGravar: () => void
  aoFechar: () => void
}) {
  const [dados, definirDados] = useState<ValoresDoBloco | null>(null)
  const [campos, definirCampos] = useState<CampoDoBloco[] | null>(null)
  const [rascunho, definirRascunho] = useState<ValoresDoBloco>({})
  const [recusa, definirRecusa] = useState<string | null>(null)
  const [gravando, definirGravando] = useState(false)
  const falta = campos === null ? null : faltaNoBloco(campos, rascunho)

  useEffect(() => {
    let vivo = true
    api
      .dadosDaConta(contaId)
      .then(async (resposta) => {
        const [cargos, setores] = await Promise.all([api.cargos(resposta.cargo_id), api.setores(resposta.entidade_id ?? undefined, resposta.setor_id)])
        if (!vivo) return
        definirCampos(comListasDoVinculo(CAMPOS_DA_CONTA, opcoesDosCargos(cargos), opcoesDosSetores(setores)))
        definirDados(valoresComIds(resposta))
      })
      .catch((falha: unknown) => definirRecusa(mensagemDeRecusa(falha)))
    return () => {
      vivo = false
    }
  }, [contaId])

  if (dados === null || campos === null) {
    return recusa !== null ? <p className={cn(RECUSA, MAIUSCULA)}>{recusa}</p> : <p className={VAZIO}>Medindo...</p>
  }

  const gravar = () => {
    const mudanca = diferenca(campos, dados, rascunho)
    if (Object.keys(mudanca).length === 0) {
      aoFechar()
      return
    }
    definirGravando(true)
    definirRecusa(null)
    api
      .alterarDadosDaConta(contaId, pedidoDoVinculo(mudanca))
      .then(() => aoGravar())
      .catch((falha: unknown) => definirRecusa(mensagemDeRecusa(falha)))
      .finally(() => definirGravando(false))
  }

  return (
    <>
      <FormularioDeCampos
        prefixo={`conta-${contaId}`}
        naGaveta
        campos={campos}
        inicial={dados}
        aoMudar={definirRascunho}
      />
      {recusa !== null && <p className={cn(RECUSA, MAIUSCULA)}>{recusa}</p>}
      <div className={RODAPE_DA_GAVETA}>
        <Button type="button" variant="outline" className={BOTAO_CANCELAR} onClick={aoFechar}>
          <Icones.Fechar />
          Cancelar
        </Button>
        <Button title={falta ?? undefined} type="button" disabled={gravando || falta !== null} onClick={gravar}>
          <Icones.Salvar />
          {gravando ? "Salvando..." : "Salvar"}
        </Button>
      </div>
    </>
  )
}

const VINCULOS_DA_CONTA = [
  { chave: "efetivo", rotulo: "Efetivo" },
  { chave: "comissionado", rotulo: "Comissionado" },
  { chave: "contratado", rotulo: "Contratado" },
  { chave: "cedido", rotulo: "Cedido" },
  { chave: "outro", rotulo: "Outro" },
]

const CAMPOS_DA_CONTA: CampoDoBloco[] = [
  { campo: "cpf", rotulo: "CPF", tipo: "cpf", obrigatorio: true },
  { campo: "matricula", rotulo: "Matrícula" },
  { campo: "cargo", rotulo: "Cargo" },
  { campo: "funcao", rotulo: "Função" },
  { campo: "lotacao", rotulo: "Lotação" },
  { campo: "vinculo", rotulo: "Vínculo", tipo: "escolha", opcoes: VINCULOS_DA_CONTA },
  { campo: "ato_de_nomeacao", rotulo: "Ato de nomeação" },
  { campo: "ato_de_nomeacao_data", rotulo: "Data do ato", tipo: "data" },
  { campo: "telefone", rotulo: "Telefone", tipo: "telefone" },
  { campo: "email", rotulo: "E-mail", tipo: "email", obrigatorio: true },
]

function NovaConta({
  papeis,
  eRoot,
  aoCriar,
  aoCancelar,
  aoRecusar,
}: {
  papeis: PapelNaTela[]
  eRoot: boolean
  aoCriar: (mensagem: string) => void
  aoCancelar: () => void
  aoRecusar: (mensagem: string) => void
}) {
  const [nome, definirNome] = useState("")
  const [email, definirEmail] = useState("")
  const [cpf, definirCpf] = useState("")
  const [senha, definirSenha] = useState("")
  const [perfis, definirPerfis] = useState<string[]>([])
  const [entidadeId, definirEntidadeId] = useState("")
  const [entidades, definirEntidades] = useState<EntidadeNaLista[]>([])
  const [setores, definirSetores] = useState<SetorNaTela[] | null>(null)
  const [setorId, definirSetorId] = useState("")
  const [enviando, definirEnviando] = useState(false)
  const [vinculando, definirVinculando] = useState(false)
  const [vinculoPor, definirVinculoPor] = useState<"email" | "cpf">("email")
  const [recusaDoVinculo, definirRecusaDoVinculo] = useState<string | null>(null)

  useEffect(() => {
    if (!eRoot) return
    api
      .entidades("")
      .then((resposta) => definirEntidades(resposta.dados))
      .catch((falha: unknown) => aoRecusar(mensagemDeRecusa(falha)))
  }, [eRoot, aoRecusar])

  useEffect(() => {
    if (eRoot && entidadeId === "") {
      definirSetores([])
      definirSetorId("")
      return
    }
    let vivo = true
    definirSetores(null)
    api
      .setores(eRoot ? Number(entidadeId) : undefined)
      .then((lista) => {
        if (!vivo) return
        definirSetores(lista)
        definirSetorId(lista.length === 1 ? String(lista[0]?.id) : "")
      })
      .catch((falha: unknown) => aoRecusar(mensagemDeRecusa(falha)))
    return () => {
      vivo = false
    }
  }, [eRoot, entidadeId, aoRecusar])

  const falta =
    nome.trim() === ""
      ? "o nome"
      : email.trim() === ""
        ? "o e-mail"
        : algarismosDoCpf(cpf) === ""
          ? "o CPF"
          : !cpfValido(cpf)
            ? "um CPF válido: os dígitos verificadores não conferem"
            : senha === ""
          ? "a senha provisória"
          : perfis.length === 0
            ? "pelo menos um perfil"
            : eRoot && entidadeId === ""
              ? "a entidade da conta"
              : setorId === ""
                ? "a lotação"
                : null

  const alternar = (papel: string, marcado: boolean) =>
    definirPerfis((atuais) => (marcado ? [...atuais.filter((p) => p !== papel), papel] : atuais.filter((p) => p !== papel)))

  const doSetorEEntidade = () => ({
    ...(setorId !== "" ? { setor_id: Number(setorId) } : {}),
    ...(eRoot ? { entidade_id: Number(entidadeId) } : {}),
  })

  const enviar = () => {
    if (falta !== null) return
    definirEnviando(true)
    api
      .criarConta({ nome: nome.trim(), email: email.trim(), cpf: algarismosDoCpf(cpf), senha, perfis, ...doSetorEEntidade() })
      .then(() => {
        definirEnviando(false)
        aoCriar(`Conta de ${nome.trim()} criada`)
      })
      .catch((falha: unknown) => {
        definirEnviando(false)
        if (falha instanceof ErroDaApi && falha.codigo === 409) {
          definirVinculoPor(falha.campo === "cpf" ? "cpf" : "email")
          definirRecusaDoVinculo(null)
          definirVinculando(true)
          return
        }
        aoRecusar(mensagemDeRecusa(falha))
      })
  }

  const vincular = () => {
    definirEnviando(true)
    api
      .vincularConta({
        ...(vinculoPor === "cpf" ? { cpf: algarismosDoCpf(cpf) } : { email: email.trim() }),
        ...doSetorEEntidade(),
      })
      .then(() => {
        definirEnviando(false)
        definirVinculando(false)
        aoCriar(`${nome.trim() === "" ? email.trim() : nome.trim()} vinculada a esta entidade`)
      })
      .catch((falha: unknown) => {
        definirEnviando(false)
        definirRecusaDoVinculo(mensagemDeRecusa(falha))
      })
  }

  return (
    <div>
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-[1_1_240px]">
          <RotuloDoCampo htmlFor="conta-nome" obrigatorio>Nome do servidor</RotuloDoCampo>
          <Input
            id="conta-nome"
            aria-required="true"
            value={nome}
            onChange={(evento) => definirNome(evento.target.value)}
          />
        </div>
        <div className="flex-[1_1_240px]">
          <RotuloDoCampo htmlFor="conta-email" obrigatorio>E-mail</RotuloDoCampo>
          <Input
            id="conta-email"
            aria-required="true"
            type="email"
            value={email}
            onChange={(evento) => definirEmail(evento.target.value)}
          />
        </div>
        <div className="flex-[1_1_180px]">
          <RotuloDoCampo htmlFor="conta-cpf" obrigatorio>CPF</RotuloDoCampo>
          <Input
            id="conta-cpf"
            aria-required="true"
            inputMode="numeric"
            autoComplete="off"
            placeholder="000.000.000-00"
            maxLength={14}
            value={cpf}
            onChange={(evento) => definirCpf(mascaraDeCpf(evento.target.value))}
          />
        </div>
      </div>

      <div className="mt-2.5 flex flex-wrap items-end gap-3">
        <div className="flex-[1_1_240px]">
          <RotuloDoCampo htmlFor="conta-senha" obrigatorio>Senha provisória</RotuloDoCampo>
          <PasswordInput
            id="conta-senha"
            aria-required="true"
            value={senha}
            onChange={(evento) => definirSenha(evento.target.value)}
          />
        </div>
        {eRoot && (
          <div className="flex-[1_1_240px]">
            <RotuloDoCampo htmlFor="conta-entidade" obrigatorio>Entidade da conta</RotuloDoCampo>
            <Escolha
              id="conta-entidade"
              obrigatorio
              valor={entidadeId}
              aoMudar={definirEntidadeId}
              vazio="Escolha a entidade"
              placeholderDaBusca="Buscar por sigla ou nome"
              opcoes={entidades.map((entidade) => ({ valor: String(entidade.id), rotulo: `${entidade.sigla} · ${entidade.nome}` }))}
            />
          </div>
        )}
        <div className="flex-[1_1_240px]">
          <RotuloDoCampo htmlFor="conta-setor" obrigatorio>Setor</RotuloDoCampo>
          <Escolha
            id="conta-setor"
            obrigatorio
            valor={setorId}
            desabilitado={setores === null || setores.length === 0}
            aoMudar={definirSetorId}
            vazio={
              setores === null
                ? "Carregando..."
                : setores.length === 0
                  ? eRoot
                    ? "Escolha a entidade primeiro"
                    : "Nenhum setor"
                  : setores.length > 1
                    ? "Escolha o setor"
                    : undefined
            }
            placeholderDaBusca="Buscar pelo nome do setor"
            opcoes={(setores ?? []).map((setor) => ({ valor: String(setor.id), rotulo: setor.caminho }))}
          />
        </div>
      </div>

      <fieldset className="mt-3">
        <legend data-slot="rotulo-do-campo" className={cn(ROTULO_DE_CAMPO, "flex items-center gap-1")}>
          Perfis
          <MarcaDeObrigatorio />
        </legend>
        <div className="grid gap-2 sm:grid-cols-2">
          {perfisOferecidos(papeis).map((p) => (
            <label
              key={p.papel}
              htmlFor={`conta-perfil-${p.papel}`}
              className="flex cursor-pointer items-start gap-2.5 rounded-(--raio-controle) border border-(--borda) px-3 py-2 hover:border-(--borda-forte) has-data-[state=checked]:border-(--marca)"
            >
              <Checkbox
                id={`conta-perfil-${p.papel}`}
                className="mt-0.5"
                checked={perfis.includes(p.papel)}
                onCheckedChange={(valor) => alternar(p.papel, valor === true)}
              />
              <span className="min-w-0">
                <span className="block text-(length:--texto-sm) font-semibold text-(--tinta)">{p.nome}</span>
                <span className={cn("block", PROCEDENCIA, MAIUSCULA)}>{p.pode}</span>
              </span>
            </label>
          ))}
        </div>
      </fieldset>

      <p className={PROCEDENCIA}>A senha precisará ser trocada no primeiro acesso.</p>

      <div className={cn(RODAPE_DA_GAVETA, "mt-4")}>
        <Button type="button" variant="outline" className={BOTAO_CANCELAR} onClick={aoCancelar}>
          <Icones.Fechar />
          Cancelar
        </Button>
        <Button
          title={falta !== null ? `Falta ${falta}` : undefined}
          type="button"
          disabled={enviando || falta !== null}
          onClick={enviar}
        >
          <Icones.Salvar />
          {enviando ? "Salvando..." : "Salvar"}
        </Button>
      </div>

      <Dialog open={vinculando} onOpenChange={definirVinculando}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Esta pessoa já tem conta no sistema</DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-3">
            <DialogDescription className="text-(--tinta) opacity-100">
              Já existe uma conta com {vinculoPor === "cpf" ? `o CPF ${mascaraDeCpf(cpf)}` : `o e-mail ${email.trim()}`}. Em vez de criar outra, vincule essa
              pessoa a esta entidade, no setor escolhido. Os perfis dela
              não mudam: eles valem para a pessoa em todas as entidades.
            </DialogDescription>
            {recusaDoVinculo !== null && (
              <p role="alert" className={cn(RECUSA, MAIUSCULA)}>{recusaDoVinculo}</p>
            )}
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => definirVinculando(false)}>
              <Icones.Fechar />
              Cancelar
            </Button>
            <Button type="button" disabled={enviando} onClick={vincular}>
              Vincular a esta entidade
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
